// NewRestaurantSetup — wizard for creating a new restaurant.
// Flow: Plan → Checkout → Search → Candidates / Manual → Request → (operator
// approves) → Provisioning → Done.
//
// The plan step comes first (prd-billing.md §3, decision D3): every
// sign-up goes through Stripe Checkout before any Keycast team exists — a
// subscription for Premium, a card check for Basic. Then the person picks
// their restaurant, and the wizard *stops*: it posts a request and shows a
// waiting screen. Nothing is provisioned until an operator approves
// (prd-restaurant-signup-approval.md). On the next login the wizard reads
// where the person stands and resumes: approved → provision + claim the
// purchase; rejected → the reason; pending → still waiting. Support and
// admin users skip all of it and provision directly, as before.

import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Store, ArrowLeft, Loader2, CheckCircle, XCircle, Sparkles, Clock } from 'lucide-react'
import { SynvyaMark } from '@synvya/portal-ui/branding'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import * as keycast from '@/lib/keycastClient'
import * as bunker from '@/lib/bunkerSigner'
import { useTeam } from '@/state/useTeam'
import { getDefaultRelays, getServerAuthorizationLabel } from '@/lib/baseUrl'
import { provisionServer, browserTimezone } from '@/services/serverRegistration'
import { searchBusinesses } from '@/services/profileBuilder'
import type { ProfileBuilderCandidate } from '@/services/profileBuilder'
import { useOnboardingCandidate } from '@/state/useOnboardingCandidate'
import { useAuth } from '@/state/useAuth'
import { useAdminStatus } from '@/state/useAdminStatus'
import { useBilling } from '@/state/useBilling'
import { consumePlanIntent } from '@/lib/planIntent'
import type { BillingInterval } from '@/lib/planIntent'
import {
  claimPurchase,
  createSignupCheckout,
  createSignupPortal,
  fetchSignupCheckoutStatus,
  fetchSignupStatus,
  submitSignupRequest,
} from '@/services/billing'
import type { Plan, SignupStatus } from '@/services/billing'
import { ApiError } from '@/lib/apiError'
import { describeFailure } from '@/lib/describeFailure'
import { lookupRegisteredRestaurant, recordGooglePlaceId } from '@/services/restaurantLookup'
import { IntervalToggle } from '@/groups/settings/pages/PlanBillingPage'
import { forgetSetupTeam, readSetupTeam, rememberSetupTeam } from '@/lib/setupTeam'

type Step =
  | 'plan-loading'
  | 'plan'
  | 'checkout-redirect'
  | 'checkout-verifying'
  | 'ready'
  | 'search'
  | 'candidates'
  | 'checking'
  | 'already-registered'
  | 'manual'
  | 'submitting'
  | 'pending'
  | 'rejected'
  | 'provisioning'
  | 'done'
  | 'error'
  | 'not-eligible'

/** How long the wizard polls for the webhook after Checkout redirects back. */
const CHECKOUT_POLL_MS = 2_000
const CHECKOUT_MAX_POLLS = 15
/** How often the waiting screen re-reads the request while it stays open. */
const PENDING_POLL_MS = 60_000

interface Props {
  onComplete: () => void
}

export function NewRestaurantSetup({ onComplete }: Props) {
  const { t } = useTranslation('onboarding')
  const { t: tb } = useTranslation('billing')
  const { loadTeams, activateTeam } = useTeam()
  const { setCandidate, setManual } = useOnboardingCandidate()
  const personalPubkey = useAuth((s) => s.personalPubkey)
  const email = useAuth((s) => s.email)
  const logout = useAuth((s) => s.logout)
  const adminRole = useAdminStatus((s) => s.role)
  const applyBilling = useBilling((s) => s.apply)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [step, setStep] = useState<Step>('plan-loading')
  const [interval, setInterval_] = useState<BillingInterval>('year')
  const [planNotice, setPlanNotice] = useState('')
  // The plan the Checkout was for; the "ready" copy and the claim key on it.
  const [plan, setPlan] = useState<Plan | null>(null)
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null)
  // The request as the server last told it, for the waiting/rejected screens.
  const [signup, setSignup] = useState<SignupStatus | null>(null)
  // True once an operator approved: the claim after provisioning keys on it.
  const [approved, setApproved] = useState(false)
  const polls = useRef(0)
  const [isSessionError, setIsSessionError] = useState(false)
  const [query, setQuery] = useState('')
  const [candidates, setCandidates] = useState<ProfileBuilderCandidate[]>([])
  const [searching, setSearching] = useState(false)
  const [restaurantName, setRestaurantName] = useState('')
  const [provisionStatus, setProvisionStatus] = useState('')
  const [error, setError] = useState('')
  // What the person picked, kept so a failed request can be retried.
  const pickRef = useRef<{ name: string; placeId: string | null; address: string | null } | null>(null)

  const startSignupCheckout = useCallback(async (chosen: Plan, chosenInterval: BillingInterval) => {
    if (!personalPubkey) {
      setPlanNotice(tb('checkoutFailed'))
      setStep('plan')
      return
    }
    setPlan(chosen)
    setStep('checkout-redirect')
    try {
      const { url } = await createSignupCheckout({ personalPubkey, email, plan: chosen, interval: chosenInterval })
      window.location.assign(url)
    } catch (err) {
      setPlanNotice(describeFailure(err, tb('checkoutFailed')))
      setStep('plan')
    }
  }, [personalPubkey, email, tb])

  /**
   * Where the server says the person stands decides the screen. Shared by
   * the first mount, the waiting screen's "check again", and its timer.
   */
  const applySignupStatus = useCallback((status: SignupStatus) => {
    setSignup(status)
    setPlan(status.plan)
    setCheckoutSessionId(status.checkout_session_id)
    switch (status.state) {
      case 'pending':
        setStep('pending')
        return true
      case 'rejected':
        setStep('rejected')
        return true
      case 'approved':
        setApproved(true)
        void resumeApproved(status)
        return true
      case 'paid':
        setStep('ready')
        return true
      default:
        return false
    }
  // resumeApproved is a plain function below; it reads nothing that changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // First mount: decide where the wizard starts.
  //   ?checkout=success   → poll the Checkout until it is done
  //   ?checkout=cancelled → the plan step, with a note
  //   support / admin     → skip the plan (comp or the merchant pays later)
  //   a request exists    → its screen: waiting, rejected, or resume setup
  //   paid, no request    → the search
  //   stored intent       → Checkout now, for the plan the website sent
  //   nothing             → the plan cards
  useEffect(() => {
    if (step !== 'plan-loading') return
    const checkout = params.get('checkout')
    const sessionId = params.get('session_id')
    if (checkout === 'success' && sessionId) {
      setCheckoutSessionId(sessionId)
      setParams({}, { replace: true })
      setStep('checkout-verifying')
      return
    }
    if (checkout === 'cancelled') {
      setParams({}, { replace: true })
      setPlanNotice(tb('checkoutCancelled'))
      consumePlanIntent()
      setStep('plan')
      return
    }
    if (adminRole !== null) {
      consumePlanIntent()
      setStep('search')
      return
    }
    if (!personalPubkey) {
      consumePlanIntent()
      setStep('plan')
      return
    }
    let cancelled = false
    fetchSignupStatus(personalPubkey)
      .then((status) => {
        if (cancelled) return
        // A standing request or a finished Checkout wins over an old intent:
        // clicking the website's card again must not open a second Checkout.
        if (applySignupStatus(status)) {
          consumePlanIntent()
          return
        }
        const intent = consumePlanIntent()
        if (intent) {
          setInterval_(intent.interval)
          void startSignupCheckout(intent.plan, intent.interval)
          return
        }
        setStep('plan')
      })
      .catch(() => {
        if (cancelled) return
        consumePlanIntent()
        setStep('plan')
      })
    return () => {
      cancelled = true
    }
  }, [step, params, setParams, adminRole, personalPubkey, startSignupCheckout, applySignupStatus, tb])

  // Back from Checkout: a subscription's webhook lands within a second or
  // two; a card check is read back from Stripe on the first poll.
  useEffect(() => {
    if (step !== 'checkout-verifying' || !checkoutSessionId) return
    let cancelled = false
    const tick = async () => {
      try {
        const status = await fetchSignupCheckoutStatus(checkoutSessionId)
        if (cancelled) return
        if (status.paid) {
          setPlan(status.plan)
          setStep('ready')
          return
        }
      } catch {
        // keep polling
      }
      if (cancelled) return
      polls.current += 1
      if (polls.current >= CHECKOUT_MAX_POLLS) {
        setPlanNotice(tb('activationSlow'))
        setStep('plan')
        return
      }
      timer = window.setTimeout(tick, CHECKOUT_POLL_MS)
    }
    let timer = window.setTimeout(tick, 0)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [step, checkoutSessionId, tb])

  // The waiting screen re-reads the request now and then, so a person who
  // left the tab open sees the approval without a new login.
  const checkAgain = useCallback(async () => {
    if (!personalPubkey) return
    try {
      const status = await fetchSignupStatus(personalPubkey)
      if (status.state !== 'pending') applySignupStatus(status)
    } catch {
      // still waiting as far as we know
    }
  }, [personalPubkey, applySignupStatus])

  useEffect(() => {
    if (step !== 'pending') return
    const timer = window.setInterval(() => { void checkAgain() }, PENDING_POLL_MS)
    return () => window.clearInterval(timer)
  }, [step, checkAgain])

  /**
   * Approved: the server holds the pick, so setup resumes from it. The
   * Google candidate is looked up again so the profile editor is prefilled
   * as it would have been; a miss falls back to the name alone.
   */
  async function resumeApproved(status: SignupStatus) {
    const name = status.restaurant_name ?? ''
    setRestaurantName(name)
    placeIdRef.current = status.google_place_id
    if (status.google_place_id) {
      try {
        const results = await searchBusinesses(name)
        const match = results.find((c) => c.placeId === status.google_place_id)
        if (match) {
          setCandidate(match)
          void startProvisioning(match.displayName, true)
          return
        }
      } catch {
        // fall through to the name alone
      }
    }
    setManual({ displayName: name })
    void startProvisioning(name, true)
  }

  async function handleSignOut() {
    await logout()
    navigate('/sign-in')
  }

  const handleManagePurchase = useCallback(async () => {
    if (!checkoutSessionId) return
    try {
      const { url } = await createSignupPortal(checkoutSessionId)
      window.location.assign(url)
    } catch (err) {
      setPlanNotice(describeFailure(err, tb('checkoutFailed')))
    }
  }, [checkoutSessionId, tb])

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return
    setSearching(true)
    setRestaurantName(query.trim())
    try {
      const results = await searchBusinesses(query.trim())
      if (results.length > 0) {
        setCandidates(results)
        setStep('candidates')
      } else {
        setStep('manual')
      }
    } catch {
      setStep('manual')
    } finally {
      setSearching(false)
    }
  }, [query])

  const [existingName, setExistingName] = useState<string | null>(null)
  // The place the restaurant is being created from, recorded on the server
  // after provisioning so the duplicate guard works before any profile exists.
  const placeIdRef = useRef<string | null>(null)
  const provisioningRef = useRef(false)

  async function handleSelectCandidate(candidate: ProfileBuilderCandidate) {
    setRestaurantName(candidate.displayName)
    setCandidate(candidate)
    placeIdRef.current = candidate.placeId || null
    // A place that is already on Synvya is somebody's restaurant, or somebody
    // has asked to be it. Stop here and say so; the fix is an invite from its
    // admin, not a second copy.
    if (candidate.placeId) {
      setStep('checking')
      const found = await lookupRegisteredRestaurant(candidate.placeId)
      if (found?.registered) {
        setExistingName(found.name || candidate.displayName)
        setStep('already-registered')
        return
      }
    }
    if (adminRole !== null) {
      startProvisioning(candidate.displayName)
      return
    }
    void submitRequest({
      name: candidate.displayName,
      placeId: candidate.placeId || null,
      address: candidate.formattedAddress || null,
    })
  }

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!restaurantName.trim()) return
    placeIdRef.current = null
    setManual({ displayName: restaurantName.trim() })
    if (adminRole !== null) {
      startProvisioning(restaurantName.trim())
      return
    }
    void submitRequest({ name: restaurantName.trim(), placeId: null, address: null })
  }

  /**
   * The end of the wizard for a self-serve sign-up: the pick becomes a
   * request, and the person waits. The session id from Checkout is the
   * credential; a person who reached the search has one.
   */
  async function submitRequest(pick: { name: string; placeId: string | null; address: string | null }) {
    pickRef.current = pick
    if (!checkoutSessionId) {
      setError(t('requestFailed'))
      setStep('error')
      return
    }
    setStep('submitting')
    setError('')
    try {
      const status = await submitSignupRequest(checkoutSessionId, {
        restaurantName: pick.name,
        googlePlaceId: pick.placeId,
        address: pick.address,
      })
      setSignup(status)
      setStep('pending')
    } catch (err) {
      if (err instanceof ApiError && err.code === 'conflict') {
        // Somebody asked for this place first, or this account already asked.
        setExistingName(pick.name)
        setStep('already-registered')
        return
      }
      setError(describeFailure(err, t('requestFailed')))
      setStep('error')
    }
  }

  /** The team an earlier, unfinished setup of this restaurant created, if Keycast still lists it. */
  async function findSetupTeam(name: string) {
    const id = readSetupTeam(personalPubkey ?? '', name)
    if (id === null) return null
    try {
      const teams = await keycast.listTeams()
      return teams.find((wire) => wire.team.id === id) ?? null
    } catch {
      return null
    }
  }

  /**
   * `claim` is passed rather than read from state: the approval path calls
   * this from a callback created on the first render, whose view of state is
   * stale, and the claim must not be skipped for that.
   */
  async function startProvisioning(name: string, claim = approved) {
    // A second call while one runs would create a second team with its own key.
    if (provisioningRef.current) return
    provisioningRef.current = true
    setStep('provisioning')
    setError('')

    try {
      // Step 1: Create team in Keycast
      setProvisionStatus(t('provisioningKey'))
      let team: any
      try {
        // A setup that failed after its team existed resumes with that team.
        team = await findSetupTeam(name)
        if (!team) {
          team = await keycast.createTeam(name)
          rememberSetupTeam(personalPubkey ?? '', name, team.team.id)
        }
      } catch (err: any) {
        console.error('[NewRestaurantSetup] createTeam failed:', {
          status: err?.status,
          message: err?.message,
          name: err?.name,
          raw: err,
        })
        const isAuthError = err?.status === 401 ||
          err?.message?.includes('UCAN') ||
          err?.message?.includes('authentication') ||
          err?.message?.includes('keycast_session')
        if (isAuthError) {
          // Session expired — redirect back to sign-in
          throw new Error(t('sessionExpired', { defaultValue: 'Your session has expired. Please sign in again.' }))
        }
        if (err?.status === 403) {
          setStep('not-eligible')
          return
        }
        throw err
      }

      const teamId = team.team.id
      const policyId = team.policies?.[0]?.policy?.id ?? 1

      // Step 2: Generate + import restaurant key
      let keyPubkey: string
      if (team.stored_keys?.length > 0) {
        keyPubkey = team.stored_keys[0].pubkey
      } else {
        // Generate a new Nostr key pair
        const { generateSecretKey, getPublicKey } = await import('nostr-tools')
        const sk = generateSecretKey()
        const pk = getPublicKey(sk)
        // Convert to nsec for import
        const { nsecEncode } = await import('nostr-tools/nip19')
        const nsec = nsecEncode(sk)
        await keycast.importTeamKey(teamId, nsec, 'Restaurant Key')
        keyPubkey = pk
      }

      // Step 3: Create authorization + connect bunker
      setProvisionStatus(t('provisioningBunker'))
      const relays = getDefaultRelays()
      const auth = await keycast.createAuthorization(teamId, keyPubkey, policyId, relays)
      if (auth.bunker_url) {
        await bunker.connect(auth.bunker_url)
      }

      // Step 4: Register with the always-on server
      setProvisionStatus(t('provisioningServer'))
      const serverLabel = getServerAuthorizationLabel()
      const serverAuth = await keycast.createAuthorization(
        teamId, keyPubkey, policyId, relays, { label: serverLabel }
      )
      if (serverAuth.bunker_url) {
        await provisionServer({
          restaurantPubkey: keyPubkey,
          serverBunkerUrl: serverAuth.bunker_url,
          authorizationLabel: serverLabel,
          timezone: browserTimezone(),
          signAsRestaurant: async (template) => bunker.signEvent(template),
        })
      }

      // Step 4a: Remember which Google place this is, so a second owner of the
      // same place is stopped at the search from now on. Best effort — a miss
      // here is a support ticket, not a failed setup.
      if (placeIdRef.current) {
        recordGooglePlaceId(
          { restaurantPubkey: keyPubkey, signAsRestaurant: async (tpl) => bunker.signEvent(tpl) },
          placeIdRef.current,
        ).catch((err) => console.warn('[NewRestaurantSetup] recording the place id failed:', err))
      }

      // Step 4b: The purchase made before this restaurant existed is now its
      // billing row — a subscription for Premium, the checked card for Basic.
      // Keycast confirms the payer runs the team; the server rewrites Stripe's
      // metadata. A failure here must not block the restaurant — the Plan
      // page offers "I already paid" for recovery.
      if (claim && personalPubkey) {
        try {
          const snapshot = await claimPurchase(
            { restaurantPubkey: keyPubkey, signAsRestaurant: async (tpl) => bunker.signEvent(tpl) },
            personalPubkey,
          )
          applyBilling(keyPubkey, snapshot)
        } catch (err) {
          console.warn('[NewRestaurantSetup] claim after provisioning failed:', err)
        }
      }

      // Step 5: Reload teams and activate the new one.
      // Use activateTeam (state-only) instead of selectTeam because the
      // bunker is already connected from Step 3. selectTeam would try to
      // reconnect/mint and can 403 for support users, clearing activeTeamId.
      await loadTeams()
      activateTeam(teamId)
      forgetSetupTeam(personalPubkey ?? '', name)

      // Done
      setStep('done')
      setTimeout(onComplete, 1500)
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Unknown error'
      // Sanitize technical messages into user-friendly ones
      let friendly = raw
      let sessionErr = false
      if (raw.includes('UCAN') || raw.includes('authentication') || raw.includes('keycast_session') || raw.includes('session has expired')) {
        friendly = t('sessionExpired', { defaultValue: 'Your session has expired. Please sign in again.' })
        sessionErr = true
      } else if (raw.includes('Failed to fetch') || raw.includes('NetworkError') || raw.includes('Load failed')) {
        friendly = t('networkError', { defaultValue: 'Could not reach the server. Check your connection and try again.' })
      } else if (raw.includes('bunker') || raw.includes('NIP-46') || raw.includes('WebSocket')) {
        friendly = t('bunkerError', { defaultValue: 'Could not connect the signing service. Please try again.' })
      }
      setIsSessionError(sessionErr)
      setError(friendly)
      setStep('error')
    } finally {
      provisioningRef.current = false
    }
  }

  const inputCls = 'w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary'

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6">
      {/* Synvya mark */}
      <span className="mb-6 flex items-center justify-center">
        <SynvyaMark variant="green" size={48} title="Synvya" />
      </span>

      <h1 className="mb-2 text-xl font-bold text-foreground">{t('welcome')}</h1>

      {/* Plan step (billing-signup.md §5.2) */}
      {step === 'plan-loading' && (
        <div className="flex flex-col items-center gap-3" data-testid="plan-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {step === 'plan' && (
        <div className="w-full max-w-md space-y-3" data-testid="plan-step">
          <h2 className="text-center text-lg font-semibold text-foreground">{tb('chooseTitle')}</h2>
          <p className="text-center text-sm text-muted-foreground">{tb('chooseSubtitle')}</p>
          {planNotice && (
            <p role="status" className="text-center text-sm text-muted-foreground">{planNotice}</p>
          )}
          <Card>
            <p className="text-sm font-semibold text-foreground">{tb('basicName')}</p>
            <p className="text-2xl font-bold text-foreground">{tb('free')}</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              <li>✓ {tb('basicFeatures.profile')}</li>
              <li>✓ {tb('basicFeatures.menu')}</li>
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">{tb('cardNotice')}</p>
            <Button variant="secondary" className="mt-3 w-full" onClick={() => startSignupCheckout('basic', interval)} disabled={!personalPubkey}>
              {tb('startFree')}
            </Button>
          </Card>
          <Card className="border-primary">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold text-foreground">{tb('premiumName')}</p>
            </div>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {(['happyHours', 'events', 'offers', 'pulse', 'printKit', 'takeout'] as const).map((k) => (
                <li key={k}>✓ {tb(`premiumFeatures.${k}`)}</li>
              ))}
            </ul>
            <IntervalToggle value={interval} onChange={setInterval_} />
            <p className="mt-2 text-xs text-muted-foreground">{tb('plusTax')}</p>
            <Button className="mt-3 w-full" onClick={() => startSignupCheckout('premium', interval)} disabled={!personalPubkey}>
              {tb('goPremium')}
            </Button>
          </Card>
        </div>
      )}

      {step === 'checkout-redirect' && (
        <div className="flex flex-col items-center gap-3" data-testid="checkout-redirect">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{tb('redirecting')}</p>
        </div>
      )}

      {step === 'checkout-verifying' && (
        <div className="flex flex-col items-center gap-3" data-testid="checkout-verifying">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            {plan === 'basic' ? tb('verifyingCard') : polls.current > 3 ? tb('verifyingSlow') : tb('verifying')}
          </p>
        </div>
      )}

      {step === 'ready' && (
        <div className="flex w-full max-w-sm flex-col items-center gap-3 text-center" data-testid="premium-ready">
          <CheckCircle className="h-8 w-8 text-emerald-400" />
          <p className="text-sm font-medium text-foreground">
            {plan === 'basic' ? tb('cardReady') : tb('premiumReady')}
          </p>
          <Button className="w-full" onClick={() => setStep('search')}>{tb('continueSetup')}</Button>
          {checkoutSessionId && plan === 'premium' && (
            <button type="button" onClick={handleManagePurchase} className="text-xs text-muted-foreground hover:underline">
              {tb('manageUnclaimed')}
            </button>
          )}
        </div>
      )}

      {/* The request is in; an operator decides. */}
      {step === 'submitting' && (
        <div className="flex flex-col items-center gap-3" data-testid="submitting">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('submitting')}</p>
        </div>
      )}

      {step === 'pending' && (
        <div className="w-full max-w-sm space-y-3 text-center" data-testid="signup-pending">
          <Clock className="mx-auto h-8 w-8 text-primary" />
          <p className="text-sm font-semibold text-foreground">{t('pendingTitle')}</p>
          <p className="text-sm text-muted-foreground">
            {t('pendingBody', { name: signup?.restaurant_name ?? restaurantName, email: email ?? '' })}
          </p>
          <Button variant="secondary" className="w-full" onClick={() => { void checkAgain() }}>
            {t('pendingCheck')}
          </Button>
          <button type="button" onClick={() => { void handleSignOut() }} className="text-xs text-muted-foreground hover:underline">
            {t('signOut')}
          </button>
        </div>
      )}

      {step === 'rejected' && (
        <div className="w-full max-w-sm space-y-3 text-center" data-testid="signup-rejected">
          <XCircle className="mx-auto h-8 w-8 text-destructive" />
          <p className="text-sm font-semibold text-foreground">
            {t('rejectedTitle', { name: signup?.restaurant_name ?? restaurantName })}
          </p>
          {signup?.reason && <p className="text-sm text-muted-foreground">{signup.reason}</p>}
          {signup?.refunded && <p className="text-sm text-muted-foreground">{t('rejectedRefund')}</p>}
          <p className="text-sm text-muted-foreground">{t('rejectedContact')}</p>
          <button type="button" onClick={() => { void handleSignOut() }} className="text-xs text-muted-foreground hover:underline">
            {t('signOut')}
          </button>
        </div>
      )}

      {/* Search step */}
      {step === 'search' && (
        <div className="w-full max-w-sm space-y-4">
          <p className="text-center text-sm text-muted-foreground">{t('searchPrompt')}</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
              placeholder={t('searchPlaceholder')}
              className={inputCls}
              autoFocus
            />
            <Button onClick={handleSearch} disabled={!query.trim() || searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          <button onClick={() => { if (query.trim()) setRestaurantName(query.trim()); setStep('manual') }} className="block w-full text-center text-sm text-primary hover:underline">
            {t('manualEntry')}
          </button>
        </div>
      )}

      {/* Candidates step */}
      {step === 'candidates' && (
        <div className="w-full max-w-sm space-y-3">
          <button onClick={() => setStep('search')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t('backToSearch')}
          </button>
          {candidates.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">{t('selectRestaurant')}</p>
              {candidates.map((c, i) => (
                <Card key={i} className="cursor-pointer hover:bg-accent" onClick={() => handleSelectCandidate(c)}>
                  <div className="flex items-center gap-3">
                    <Store className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">{c.displayName}</p>
                      {c.formattedAddress && <p className="truncate text-xs text-muted-foreground">{c.formattedAddress}</p>}
                    </div>
                    <span className="text-xs font-medium text-primary">{t('candidateSelect')}</span>
                  </div>
                </Card>
              ))}
            </>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">{t('noResults')}</p>
          )}
          <button onClick={() => { if (query.trim()) setRestaurantName(query.trim()); setStep('manual') }} className="block w-full text-center text-sm text-primary hover:underline">
            {t('manualEntry')}
          </button>
        </div>
      )}

      {/* Already on Synvya (duplicate guard) */}
      {step === 'checking' && (
        <div className="flex flex-col items-center gap-3" data-testid="checking-registered">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('checkingRegistered')}</p>
        </div>
      )}

      {step === 'already-registered' && (
        <div className="w-full max-w-sm space-y-3 text-center" data-testid="already-registered">
          <Store className="mx-auto h-8 w-8 text-primary" />
          <p className="text-sm font-semibold text-foreground">
            {t('alreadyRegisteredTitle', { name: existingName ?? restaurantName })}
          </p>
          <p className="text-sm text-muted-foreground">{t('alreadyRegisteredBody')}</p>
          <Button variant="secondary" className="w-full" onClick={() => setStep('search')}>
            {t('backToSearch')}
          </Button>
        </div>
      )}

      {/* Manual entry step */}
      {step === 'manual' && (
        <div className="w-full max-w-sm space-y-4">
          <button onClick={() => setStep('search')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t('backToSearch')}
          </button>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <label htmlFor="restaurantName" className="mb-1 block text-sm font-medium text-foreground">
                {t('manualName')}
              </label>
              <input
                id="restaurantName"
                type="text"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                placeholder={t('manualNamePlaceholder')}
                className={inputCls}
                required
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={!restaurantName.trim()}>
              {t('createButton')}
            </Button>
          </form>
        </div>
      )}

      {/* Provisioning step */}
      {step === 'provisioning' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{provisionStatus || t('provisioning')}</p>
        </div>
      )}

      {/* Done step */}
      {step === 'done' && (
        <div className="flex flex-col items-center gap-3">
          <CheckCircle className="h-8 w-8 text-emerald-400" />
          <p className="text-sm font-medium text-emerald-400">{t('provisioningDone')}</p>
        </div>
      )}

      {/* Error step */}
      {step === 'error' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <XCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-semibold text-foreground">{t('provisioningError')}</p>
          <p className="max-w-xs text-sm text-muted-foreground">{error}</p>
          {isSessionError ? (
            <Button onClick={() => navigate('/sign-in')} className="mt-2">
              {t('signInAgain', { defaultValue: 'Sign in again' })}
            </Button>
          ) : pickRef.current && !approved && adminRole === null ? (
            <Button onClick={() => { void submitRequest(pickRef.current!) }} className="mt-2">
              {t('retryButton')}
            </Button>
          ) : (
            <Button onClick={() => startProvisioning(restaurantName)} className="mt-2">
              {t('retryButton')}
            </Button>
          )}
        </div>
      )}

      {/* Not eligible step */}
      {step === 'not-eligible' && (
        <div className="max-w-xs text-center">
          <p className="text-sm text-muted-foreground">{t('notEligible')}</p>
        </div>
      )}
    </div>
  )
}
